/* eslint-disable react-hooks/exhaustive-deps */
import { HubConnectionState } from '@microsoft/signalr';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { BulkEditStatus } from 'src/components/common/BulkActions/BulkEditStatus';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useBulkEditHubContext } from 'src/contexts/BulkEditHubContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { EventMapContextProvider } from 'src/contexts/EventMapContext';
import { useMultiSelectionContext } from 'src/contexts/MultiSelectionContext';
import { Stack } from 'src/core/ui';
import {
  AutoPricingSettingsInput,
  PricingSettingsUpdateKey,
} from 'src/dialogs/BulkEdits/BulkEditAutoPricingSettingsDialog/BulkEditAutoPricingSettingsDialog.types';
import {
  generateDefaultSettings,
  getAutoPricingSettingsUpdateInput,
  getSettingsInputChanged,
  santizedPricingSettingsUpdateInput,
} from 'src/dialogs/BulkEdits/BulkEditAutoPricingSettingsDialog/BulkEditAutoPricingSettingsDialog.utils';
import { PricingSettingsPreview } from 'src/dialogs/BulkEdits/BulkEditAutoPricingSettingsDialog/PricingSettingsPreview';
import { BulkEditFooter } from 'src/dialogs/BulkEdits/common/BulkEditFooter';
import { useBulkEditHub } from 'src/hooks/useBulkEditHub';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import {
  ActionOutboxEntityType,
  BulkActionType,
  BulkEditPreviewWithDetails,
  BulkEditProgress,
  BulkEditStep,
  BulkListingPriceCalculationResult,
  Event,
  Feature,
  Listing,
  ListingDetails,
  ListingDetailsAutoPricingSectionUpdatesV2,
} from 'src/WebApiController';

import { MobileAutoPricingSettings } from './BulkEditAutoPricingSettingsMobileContent.components';

export type BulkEditAutoPricingSettingsMobileContentProps = {
  updateKey: string;
  isLoading?: boolean;
  listings?: Listing[] | null;
  selectedListingIds?: number[];
  event?: Event;
  canUseZoneMap?: boolean;
  multiEvents?: boolean;
  onOkay: (
    input: ListingDetailsAutoPricingSectionUpdatesV2 | null,
    supportBackgroundProcess?: boolean,
    onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
    preview?: BulkEditPreviewWithDetails
  ) => void;
  onCancel?: () => void;
  onPreviewCalculated?: (
    result: BulkListingPriceCalculationResult | null
  ) => void;
};

export const BulkEditAutoPricingSettingsMobileContent: React.FC<
  BulkEditAutoPricingSettingsMobileContentProps
> = ({ listings, selectedListingIds, event, ...rest }) => {
  const { loginContext } = useAppContext();
  const hasAllowSeatScoreCompListingModeFeature = useUserHasFeature(
    Feature.AllowSeatScoreCompListingMode
  );
  const selectedListings = useMemo(() => {
    const flattenedListings = listings?.flatMap((l) => flattenListingGroup(l));
    return (
      flattenedListings
        ?.filter((l) => l.isFull && selectedListingIds?.includes(l.id))
        .map((l) => l as Listing) ?? []
    );
  }, [listings, selectedListingIds]);

  const isMultiEvent = useMemo(() => rest.multiEvents, [rest.multiEvents]);

  const settingsInput = useMemo(
    () => generateDefaultSettings(selectedListings, Boolean(isMultiEvent)),
    [selectedListings, isMultiEvent]
  );

  const defaultValues = useMemo(
    () =>
      getAutoPricingSettingsUpdateInput(
        settingsInput,
        loginContext?.user?.activeAccount?.currencyCode ?? null,
        hasAllowSeatScoreCompListingModeFeature
      ),
    [
      settingsInput,
      loginContext?.user?.activeAccount?.currencyCode,
      hasAllowSeatScoreCompListingModeFeature,
    ]
  );
  const methods = useForm<ListingDetailsAutoPricingSectionUpdatesV2>({
    defaultValues,
  });

  return (
    <FormProvider {...methods}>
      <EventMapContextProvider event={event}>
        <BulkEditAutoPricingSettingsMobileContentDetails
          settingsInput={settingsInput}
          updateKey={rest.updateKey}
          onOkay={rest.onOkay}
          onCancel={rest.onCancel}
          canUseZoneMap={rest.canUseZoneMap}
          isLoading={rest.isLoading}
        />
      </EventMapContextProvider>
    </FormProvider>
  );
};

const BulkEditAutoPricingSettingsMobileContentDetails = ({
  updateKey,
  isLoading,
  onOkay,
  onCancel,
  settingsInput,
  canUseZoneMap,
}: BulkEditAutoPricingSettingsMobileContentProps & {
  settingsInput: AutoPricingSettingsInput;
}) => {
  const { setActivePosEntity } = useActivePosEntityContext<ListingDetails>();
  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();
  const { setSelectionMode } = useMultiSelectionContext();
  const { setProgress, setPreview, setMainDialogOpened } =
    useBulkEditHubContext();

  const [changedFields, setChangedFileds] =
    useState<Partial<Record<PricingSettingsUpdateKey, boolean | undefined>>>();
  const { handleSubmit, watch, reset, formState } =
    useFormContext<ListingDetailsAutoPricingSectionUpdatesV2>();

  const input = watch();

  const onClose = useCallback(
    async (
      newProgress?: BulkEditProgress,
      closeMainDialogForPopover?: boolean
    ) => {
      if (newProgress?.step === BulkEditStep.Done) {
        setIsRefreshing(true);
        setActivePosEntity(0);
        await refreshExpandedListItems();
        setSelectionMode(undefined);
        if (closeMainDialogForPopover) {
          setMainDialogOpened(false);
        } else {
          setProgress(undefined);
          setPreview(undefined);
        }
        setIsRefreshing(false);
        // progress kept to allow the BulkEditStatusPopover to show the result
      }
      onCancel?.();
    },
    [
      onCancel,
      refreshExpandedListItems,
      setActivePosEntity,
      setMainDialogOpened,
      setPreview,
      setProgress,
      setSelectionMode,
    ]
  );

  const onBulkEditDone = useCallback(
    async (doneProgress: BulkEditProgress, finalErrors: string[]) => {
      if (finalErrors.length === 0 && doneProgress.step === BulkEditStep.Done) {
        onClose(doneProgress, true);
      }
    },
    [onClose]
  );

  const { bulkEditHub, preview, initJob, stage } = useBulkEditHub(
    ActionOutboxEntityType.Listing,
    BulkActionType.UpdateAutoPricingSettings,
    updateKey,
    onBulkEditDone
  );

  const hasBackgroundBulkEditFeature = useMemo(() => {
    return bulkEditHub?.state === HubConnectionState.Connected;
  }, [bulkEditHub]);

  const onSubmitHandler = useCallback(
    (onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void) => {
      if (onPreviewReceived) {
        onOkay(null, false, onPreviewReceived);
      } else {
        handleSubmit((i) => {
          onOkay(
            santizedPricingSettingsUpdateInput(i, changedFields),
            hasBackgroundBulkEditFeature,
            undefined,
            preview
          );
        })();
      }
    },
    [onOkay, handleSubmit, changedFields, hasBackgroundBulkEditFeature, preview]
  );

  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    initJob();
  }, []);

  useEffect(() => {
    const changed = getSettingsInputChanged(
      input,
      formState.defaultValues as ListingDetailsAutoPricingSectionUpdatesV2,
      settingsInput
    );

    setChangedFileds(changed);
  }, [
    JSON.stringify(input),
    JSON.stringify(formState.defaultValues),
    JSON.stringify(settingsInput),
  ]);

  const hasChanges = useMemo(
    () => Object.values(changedFields ?? {}).some((value) => !!value),
    [changedFields]
  );

  const submittButtonRef = useRef<HTMLButtonElement>(null);

  const onUpdateChangedFields = useCallback(
    (
      values: Partial<Record<PricingSettingsUpdateKey, boolean | undefined>>
    ) => {
      setChangedFileds((prev) => ({ ...prev, ...values }));
    },
    [setChangedFileds]
  );

  return (
    <Stack direction="column" width="full" height="full">
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        <BulkEditStatus
          entityType={ActionOutboxEntityType.Listing}
          isLoading={isRefreshing || isLoading}
          updateKey={updateKey}
          height="fit"
          entityUpdatesPreview={
            <PricingSettingsPreview
              settingsInput={settingsInput}
              defaultValues={
                formState.defaultValues as ListingDetailsAutoPricingSectionUpdatesV2
              }
              updates={input}
              changedFields={changedFields}
              updateChangedFields={onUpdateChangedFields}
            />
          }
        >
          <Stack direction="column" width="full" gap="s">
            <MobileAutoPricingSettings
              settingsInput={settingsInput}
              canUseZoneMap={canUseZoneMap}
            />
          </Stack>
        </BulkEditStatus>
      </div>

      <Stack justifyContent="spaceBetween" width="full">
        <BulkEditFooter
          entityType={ActionOutboxEntityType.Listing}
          isLoading={isLoading}
          hasBackgroundBulkEditFeature={hasBackgroundBulkEditFeature}
          onClose={onClose}
          onSubmit={onSubmitHandler}
          disabled={!hasChanges}
          submittButtonRef={submittButtonRef}
        />
      </Stack>
    </Stack>
  );
};
